import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listings, Listing, createAlertApi } from '../api/client';
import { config } from '../config';
import ListingCard from '../components/ListingCard';
import { AMENITIES, getAmenityLabel } from '../data/amenities';

const UNIT_TYPES = [
  { value: 'apartment', labelKey: 'form.unitTypeApartment' },
  { value: 'villa', labelKey: 'form.unitTypeVilla' },
  { value: 'twin_house', labelKey: 'form.unitTypeTwinHouse' },
  { value: 'town', labelKey: 'form.unitTypeTown' },
  { value: 'studio', labelKey: 'form.unitTypeStudio' },
  { value: 'room', labelKey: 'form.unitTypeRoom' },
] as const;

export default function Home() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';

  const typeFromUrl = searchParams.get('type') || '';
  const locationFromUrl = searchParams.get('location') || '';
  const countryFromUrl = searchParams.get('country') || '';
  const minPriceFromUrl = searchParams.get('minPrice') || '';
  const maxPriceFromUrl = searchParams.get('maxPrice') || '';
  const bedroomsFromUrl = searchParams.get('bedrooms') || '';
  const categoryFromUrl = searchParams.get('category') || '';
  const unitTypeFromUrl = searchParams.get('unitType') || '';
  const amenitiesFromUrl = searchParams.get('amenities') || '';
  const sortFromUrl = searchParams.get('sort') || 'newest';
  const orderFromUrl = searchParams.get('order') || 'desc';

  const [items, setItems] = useState<Listing[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [pageStack, setPageStack] = useState<Array<{ token: string; items: Listing[] }>>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [type, setType] = useState(typeFromUrl);

  // Local filter state for the form (apply on button click to update URL)
  const [localLocation, setLocalLocation] = useState(locationFromUrl);
  const [localCountry, setLocalCountry] = useState(countryFromUrl);
  const [localMinPrice, setLocalMinPrice] = useState(minPriceFromUrl);
  const [localMaxPrice, setLocalMaxPrice] = useState(maxPriceFromUrl);
  const [localBedrooms, setLocalBedrooms] = useState(bedroomsFromUrl);
  const [localCategory, setLocalCategory] = useState(categoryFromUrl);
  const [localUnitType, setLocalUnitType] = useState(unitTypeFromUrl);
  const [localAmenities, setLocalAmenities] = useState<string[]>(
    amenitiesFromUrl ? amenitiesFromUrl.split(',').map((a) => a.trim()).filter(Boolean) : []
  );
  const [alertEmail, setAlertEmail] = useState('');
  const [alertSubmitting, setAlertSubmitting] = useState(false);
  const [alertDone, setAlertDone] = useState(false);
  const [alertError, setAlertError] = useState<string | null>(null);

  useEffect(() => {
    setType(typeFromUrl);
  }, [typeFromUrl]);

  useEffect(() => {
    setLocalLocation(locationFromUrl);
    setLocalCountry(countryFromUrl);
    setLocalMinPrice(minPriceFromUrl);
    setLocalMaxPrice(maxPriceFromUrl);
    setLocalBedrooms(bedroomsFromUrl);
    setLocalCategory(categoryFromUrl);
    setLocalUnitType(unitTypeFromUrl);
    setLocalAmenities(amenitiesFromUrl ? amenitiesFromUrl.split(',').map((a) => a.trim()).filter(Boolean) : []);
  }, [locationFromUrl, countryFromUrl, minPriceFromUrl, maxPriceFromUrl, bedroomsFromUrl, categoryFromUrl, unitTypeFromUrl, amenitiesFromUrl]);

  const PAGE_SIZE = 12;
  const listParams: import('../api/client').ListingListParams = {
    type: type || undefined,
    limit: String(PAGE_SIZE),
    location: locationFromUrl.trim() || undefined,
    country: countryFromUrl.trim() || undefined,
    minPrice: minPriceFromUrl.trim() || undefined,
    maxPrice: maxPriceFromUrl.trim() || undefined,
    bedrooms: bedroomsFromUrl.trim() || undefined,
    category: categoryFromUrl.trim() || undefined,
    unitType: unitTypeFromUrl.trim() || undefined,
    amenities: amenitiesFromUrl.trim() || undefined,
    sort: (sortFromUrl === 'price' || sortFromUrl === 'area' || sortFromUrl === 'bedrooms' || sortFromUrl === 'newest') ? sortFromUrl : undefined,
    order: (orderFromUrl === 'asc' || orderFromUrl === 'desc') ? orderFromUrl : undefined,
  };

  useEffect(() => {
    let cancelled = false;
    loadingMoreRef.current = false;
    setLoading(true);
    setError(null);
    setNextToken(undefined);
    setPageStack([]);
    const params: import('../api/client').ListingListParams = {
      type: type || undefined,
      limit: String(PAGE_SIZE),
      location: locationFromUrl.trim() || undefined,
      country: countryFromUrl.trim() || undefined,
      minPrice: minPriceFromUrl.trim() || undefined,
      maxPrice: maxPriceFromUrl.trim() || undefined,
      bedrooms: bedroomsFromUrl.trim() || undefined,
      category: categoryFromUrl.trim() || undefined,
      unitType: unitTypeFromUrl.trim() || undefined,
      amenities: amenitiesFromUrl.trim() || undefined,
      sort: (sortFromUrl === 'price' || sortFromUrl === 'area' || sortFromUrl === 'bedrooms' || sortFromUrl === 'newest') ? sortFromUrl : undefined,
      order: (orderFromUrl === 'asc' || orderFromUrl === 'desc') ? orderFromUrl : undefined,
    };
    listings
      .list(params)
      .then((res) => {
        if (!cancelled) {
          const raw = res as Record<string, unknown>;
          const next = (res.nextToken ?? raw.next_token ?? raw.nextToken) as string | undefined;
          setItems(res.items ?? []);
          setNextToken(next ?? undefined);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setItems([]);
          setError(err instanceof Error ? err.message : 'Could not load listings');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [
    type,
    locationFromUrl,
    countryFromUrl,
    minPriceFromUrl,
    maxPriceFromUrl,
    bedroomsFromUrl,
    categoryFromUrl,
    unitTypeFromUrl,
    amenitiesFromUrl,
    sortFromUrl,
    orderFromUrl,
  ]);

  const loadMore = () => {
    const token = nextToken;
    if (!token || loadingMore || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setError(null);
    // Store current page (12 items) so Previous can restore it
    if (items.length > 0 && token) {
      setPageStack((prev) => [...prev, { token, items: [...items] }]);
    }
    const nextPageParams = { ...listParams, nextToken: token, limit: String(PAGE_SIZE) };
    listings
      .list(nextPageParams)
      .then((res) => {
        const raw = res as Record<string, unknown>;
        const next = (res.nextToken ?? raw.next_token ?? raw.nextToken) as string | undefined;
        setItems(res.items ?? []);
        setNextToken(next ?? undefined);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Could not load next page');
      })
      .finally(() => {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  };

  const goToPreviousPage = () => {
    if (pageStack.length === 0 || loadingMore) return;
    const stack = [...pageStack];
    const prev = stack.pop();
    if (!prev) return;
    setPageStack(stack);
    setItems(prev.items);
    setNextToken(prev.token);
  };

  const currentPage = pageStack.length + 1;

  const goToFirstPage = () => {
    if (currentPage === 1 || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    setPageStack([]);
    setNextToken(undefined);
    listings
      .list(listParams)
      .then((res) => {
        const next = res.nextToken ?? (res as { next_token?: string }).next_token;
        setItems(res.items ?? []);
        setNextToken(next ?? undefined);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load'))
      .finally(() => setLoadingMore(false));
  };

  const goToPage = (n: number) => {
    if (loadingMore) return;
    if (n === 1) {
      goToFirstPage();
      return;
    }
    if (n < currentPage) {
      const popCount = currentPage - n;
      const stack = [...pageStack];
      if (popCount > stack.length || popCount < 1) return;
      const prev = stack[popCount - 1];
      const newStack = stack.slice(0, popCount - 1);
      setPageStack(newStack);
      if (prev) {
        setItems(prev.items);
        setNextToken(prev.token);
      }
      return;
    }
    if (n === currentPage + 1 && nextToken) loadMore();
  };

  const canGoNext = !!nextToken;
  const canGoPrevious = pageStack.length > 0;
  const rangeFrom = items.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeTo = (currentPage - 1) * PAGE_SIZE + items.length;

  const applyFilters = useCallback(() => {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (localLocation.trim()) params.set('location', localLocation.trim());
    if (localCountry.trim()) params.set('country', localCountry.trim());
    if (localMinPrice.trim()) params.set('minPrice', localMinPrice.trim());
    if (localMaxPrice.trim()) params.set('maxPrice', localMaxPrice.trim());
    if (localBedrooms.trim()) params.set('bedrooms', localBedrooms.trim());
    if (localCategory) params.set('category', localCategory);
    if (localUnitType) params.set('unitType', localUnitType);
    if (localAmenities.length > 0) params.set('amenities', localAmenities.join(','));
    if (sortFromUrl && ['newest', 'price', 'area', 'bedrooms'].includes(sortFromUrl)) params.set('sort', sortFromUrl);
    if (orderFromUrl && ['asc', 'desc'].includes(orderFromUrl)) params.set('order', orderFromUrl);
    setSearchParams(params, { replace: true });
  }, [
    type,
    localLocation,
    localCountry,
    localMinPrice,
    localMaxPrice,
    localBedrooms,
    localCategory,
    localUnitType,
    localAmenities,
    sortFromUrl,
    orderFromUrl,
    setSearchParams,
  ]);

  const clearFilters = useCallback(() => {
    setLocalLocation('');
    setLocalCountry('');
    setLocalMinPrice('');
    setLocalMaxPrice('');
    setLocalBedrooms('');
    setLocalCategory('');
    setLocalUnitType('');
    setLocalAmenities([]);
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    setSearchParams(params, { replace: true });
  }, [type, setSearchParams]);

  const toggleAmenity = (key: string) => {
    setLocalAmenities((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const hasActiveFilters =
    locationFromUrl ||
    countryFromUrl ||
    minPriceFromUrl ||
    maxPriceFromUrl ||
    bedroomsFromUrl ||
    categoryFromUrl ||
    unitTypeFromUrl ||
    amenitiesFromUrl;

  const setSort = (sort: string, order: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('sort', sort);
    params.set('order', order);
    setSearchParams(params, { replace: true });
  };

  const submitAlert = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!alertEmail.trim()) return;
    setAlertSubmitting(true);
    setAlertError(null);
    try {
      const filters: Record<string, string> = {};
      if (type) filters.type = type;
      if (locationFromUrl) filters.location = locationFromUrl;
      if (countryFromUrl) filters.country = countryFromUrl;
      if (minPriceFromUrl) filters.minPrice = minPriceFromUrl;
      if (maxPriceFromUrl) filters.maxPrice = maxPriceFromUrl;
      if (bedroomsFromUrl) filters.bedrooms = bedroomsFromUrl;
      if (categoryFromUrl) filters.category = categoryFromUrl;
      if (unitTypeFromUrl) filters.unitType = unitTypeFromUrl;
      if (amenitiesFromUrl) filters.amenities = amenitiesFromUrl;
      await createAlertApi(alertEmail.trim(), filters);
      setAlertDone(true);
    } catch (err) {
      setAlertError(err instanceof Error ? err.message : 'Failed to save alert');
    } finally {
      setAlertSubmitting(false);
    }
  };

  const typeLabel = type === 'sale' ? t('common.forSale') : type === 'rent' ? t('common.forRent') : type === 'short_term' ? t('common.shortTerm') : '';

  const filterSidebar = (
    <div
      className="home-filter-sidebar"
      style={{
        width: 280,
        flexShrink: 0,
        padding: '1rem',
        background: '#f9fafb',
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        height: 'fit-content',
        position: 'sticky',
        top: 24,
      }}
    >
      <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '1rem', color: '#111' }}>
        {t('common.filters')}
      </h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            {t('common.categoryLabel')}
          </label>
          <select
            value={localCategory}
            onChange={(e) => setLocalCategory(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              borderRadius: 8,
              border: '1px solid #d1d5db',
              fontSize: '0.9rem',
              background: '#fff',
            }}
          >
            <option value="">{t('common.anyCategory')}</option>
            <option value="residential">{t('common.residential')}</option>
            <option value="commercial">{t('common.commercial')}</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            {t('common.unitTypeLabel')}
          </label>
          <select
            value={localUnitType}
            onChange={(e) => setLocalUnitType(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              borderRadius: 8,
              border: '1px solid #d1d5db',
              fontSize: '0.9rem',
              background: '#fff',
            }}
          >
            <option value="">{t('common.anyPropertyType')}</option>
            {UNIT_TYPES.map((u) => (
              <option key={u.value} value={u.value}>
                {t(u.labelKey)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            {t('common.locationPlaceholder')}
          </label>
          <input
            type="text"
            value={localLocation}
            onChange={(e) => setLocalLocation(e.target.value)}
            placeholder={t('common.searchPlaceholder')}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              borderRadius: 8,
              border: '1px solid #d1d5db',
              fontSize: '0.9rem',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            {t('common.minPrice')}
          </label>
          <input
            type="number"
            min={0}
            value={localMinPrice}
            onChange={(e) => setLocalMinPrice(e.target.value)}
            placeholder="0"
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              borderRadius: 8,
              border: '1px solid #d1d5db',
              fontSize: '0.9rem',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            {t('common.maxPrice')}
          </label>
          <input
            type="number"
            min={0}
            value={localMaxPrice}
            onChange={(e) => setLocalMaxPrice(e.target.value)}
            placeholder="—"
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              borderRadius: 8,
              border: '1px solid #d1d5db',
              fontSize: '0.9rem',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            {t('form.bedrooms')}
          </label>
          <input
            type="number"
            min={0}
            value={localBedrooms}
            onChange={(e) => setLocalBedrooms(e.target.value)}
            placeholder="—"
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              borderRadius: 8,
              border: '1px solid #d1d5db',
              fontSize: '0.9rem',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            {t('common.amenities')}
          </label>
          <div
            style={{
              maxHeight: 180,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              padding: '0.5rem 0',
            }}
          >
            {AMENITIES.slice(0, 20).map((a) => (
              <label
                key={a.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: '0.85rem',
                  color: '#374151',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={localAmenities.includes(a.key)}
                  onChange={() => toggleAmenity(a.key)}
                />
                {getAmenityLabel(a.key, lang)}
              </label>
            ))}
            {AMENITIES.length > 20 && (
              <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                +{AMENITIES.length - 20} more
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={applyFilters}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: 8,
              background: '#0d9488',
              color: '#fff',
              border: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              cursor: 'pointer',
            }}
          >
            {t('common.applyFilters')}
          </button>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: 8,
                background: '#fff',
                color: '#374151',
                border: '1px solid #d1d5db',
                fontWeight: 500,
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              {t('common.clearFilters')}
            </button>
          )}
        </div>

        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            {t('common.notifyMeWhenNew')}
          </label>
          {alertDone ? (
            <p style={{ fontSize: '0.85rem', color: '#059669', margin: 0 }}>{t('common.alertSaved')}</p>
          ) : (
            <form onSubmit={submitAlert}>
              <input
                type="email"
                value={alertEmail}
                onChange={(e) => setAlertEmail(e.target.value)}
                placeholder={t('common.yourEmail')}
                required
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  fontSize: '0.9rem',
                  marginBottom: 6,
                  boxSizing: 'border-box',
                }}
              />
              <button
                type="submit"
                disabled={alertSubmitting}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: 8,
                  background: '#f0fdfa',
                  color: '#0d9488',
                  border: '1px solid #99f6e4',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  cursor: alertSubmitting ? 'not-allowed' : 'pointer',
                }}
              >
                {alertSubmitting ? t('common.loading') : t('common.saveAlert')}
              </button>
              {alertError && <p style={{ fontSize: '0.8rem', color: '#b91c1c', marginTop: 4 }}>{alertError}</p>}
            </form>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      {/* Type tabs */}
      <div
        className="home-type-tabs"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '1rem 0',
          borderBottom: '1px solid #e5e7eb',
          marginBottom: '1.25rem',
        }}
      >
        <Link
          to="/listings"
          style={{
            padding: '0.5rem 1rem',
            borderRadius: 8,
            background: !type ? '#0d9488' : 'transparent',
            color: !type ? '#fff' : '#374151',
            fontWeight: 600,
            fontSize: '0.9rem',
            textDecoration: 'none',
          }}
        >
          {t('common.all')}
        </Link>
        <Link
          to="/listings?type=sale"
          style={{
            padding: '0.5rem 1rem',
            borderRadius: 8,
            background: type === 'sale' ? '#0d9488' : 'transparent',
            color: type === 'sale' ? '#fff' : '#374151',
            fontWeight: 600,
            fontSize: '0.9rem',
            textDecoration: 'none',
          }}
        >
          {t('common.forSale')}
        </Link>
        <Link
          to="/listings?type=rent"
          style={{
            padding: '0.5rem 1rem',
            borderRadius: 8,
            background: type === 'rent' ? '#0d9488' : 'transparent',
            color: type === 'rent' ? '#fff' : '#374151',
            fontWeight: 600,
            fontSize: '0.9rem',
            textDecoration: 'none',
          }}
        >
          {t('common.forRent')}
        </Link>
        <Link
          to="/listings?type=short_term"
          style={{
            padding: '0.5rem 1rem',
            borderRadius: 8,
            background: type === 'short_term' ? '#0d9488' : 'transparent',
            color: type === 'short_term' ? '#fff' : '#374151',
            fontWeight: 600,
            fontSize: '0.9rem',
            textDecoration: 'none',
          }}
        >
          {t('common.shortTerm')}
        </Link>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: '0.9rem', color: '#6b7280', marginRight: 8 }}>{t('common.sortBy')}</span>
        <select
          value={`${sortFromUrl}-${orderFromUrl}`}
          onChange={(e) => {
            const v = e.target.value;
            const [s, o] = v.split('-');
            setSort(s, o);
          }}
          style={{
            padding: '0.4rem 0.6rem',
            borderRadius: 8,
            border: '1px solid #d1d5db',
            fontSize: '0.9rem',
            background: '#fff',
          }}
        >
          <option value="newest-desc">{t('common.sortNewest')}</option>
          <option value="price-asc">{t('common.sortPriceAsc')}</option>
          <option value="price-desc">{t('common.sortPriceDesc')}</option>
          <option value="area-desc">{t('common.sortAreaDesc')}</option>
          <option value="area-asc">{t('common.sortAreaAsc')}</option>
          <option value="bedrooms-desc">{t('common.sortBedroomsDesc')}</option>
          <option value="bedrooms-asc">{t('common.sortBedroomsAsc')}</option>
        </select>
        <Link
          to={`/listings/map${searchParams.toString() ? `?${searchParams.toString()}` : ''}`}
          style={{
            marginLeft: 12,
            padding: '0.4rem 0.75rem',
            borderRadius: 8,
            border: '1px solid #0d9488',
            color: '#0d9488',
            fontWeight: 600,
            fontSize: '0.9rem',
            textDecoration: 'none',
          }}
        >
          {t('common.mapView')}
        </Link>
      </div>

      <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.5rem', color: '#111' }}>
        {typeLabel ? typeLabel : t('common.realEstate')}
      </h1>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
        {t('common.refineSearch')}
      </p>
      <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '0.5rem' }}>
        {t('common.showingRange', { from: rangeFrom, to: rangeTo })}
        {nextToken ? ` · ${t('common.loadMoreHint')}` : ''}
        {typeLabel ? ` — ${typeLabel}` : ''}
      </p>
      <div className="home-page-layout" style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Filters sidebar */}
        {filterSidebar}
        <div className="home-page-results" style={{ minWidth: 300, flex: 1 }}>
          {loading && (
            <div className="home-listings-grid" aria-busy="true" aria-label={t('common.loading')}>
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="home-listing-skeleton">
                  <div className="home-listing-skeleton-img" />
                  <div className="home-listing-skeleton-line" style={{ width: '80%', marginTop: 12 }} />
                  <div className="home-listing-skeleton-line" style={{ width: '50%', marginTop: 8 }} />
                  <div className="home-listing-skeleton-line" style={{ width: '60%', marginTop: 8 }} />
                </div>
              ))}
            </div>
          )}
          {error && (
            <div
              style={{
                padding: '1.5rem 2rem',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 12,
                marginBottom: '1.5rem',
              }}
            >
              <p style={{ color: '#b91c1c', fontWeight: 600, marginBottom: '0.5rem' }}>{t('home.couldNotLoadListings')}</p>
              <p style={{ color: '#991b1b', fontSize: '0.9rem', marginBottom: '0.5rem' }}>{error}</p>
              {!config.apiBaseUrl && (
                <p style={{ fontSize: '0.875rem', color: '#b91c1c' }}>
                  {t('home.apiUrlNotSet')}
                </p>
              )}
              <p style={{ fontSize: '0.875rem', color: '#991b1b' }}>
                {t('home.amplifyHint')}
              </p>
              <p style={{ fontSize: '0.875rem', color: '#991b1b', marginTop: '0.5rem' }}>
                {t('home.amplifyHintAlreadySet')}
              </p>
            </div>
          )}
          {!loading && items.length > 0 && (
            <>
              <div className="home-listings-grid">
                {items.map((listing) => (
                  <ListingCard key={listing.listingId} listing={listing} />
                ))}
              </div>
              <nav
                role="navigation"
                aria-label="Listings pagination"
                className="home-pagination"
              >
                <p className="home-pagination-range" aria-live="polite">
                  {t('common.paginationRange', { from: rangeFrom, to: rangeTo })}
                </p>
                <div className="home-pagination-buttons">
                  <button
                    type="button"
                    onClick={goToFirstPage}
                    disabled={loadingMore || currentPage === 1}
                    className="home-pagination-btn home-pagination-btn--arrow"
                    aria-label={t('common.firstPage')}
                    title={t('common.firstPage')}
                  >
                    ‹‹
                  </button>
                  <button
                    type="button"
                    onClick={goToPreviousPage}
                    disabled={loadingMore || !canGoPrevious}
                    className="home-pagination-btn home-pagination-btn--arrow"
                    aria-label={t('common.previousPage')}
                    title={t('common.previousPage')}
                  >
                    ‹
                  </button>
                  {Array.from({ length: currentPage + (canGoNext ? 1 : 0) }, (_, i) => i + 1).map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => goToPage(num)}
                      disabled={loadingMore || num === currentPage}
                      className={`home-pagination-btn home-pagination-btn--num ${num === currentPage ? 'home-pagination-btn--active' : ''}`}
                      aria-label={t('common.pageN', { n: num })}
                      aria-current={num === currentPage ? 'page' : undefined}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore || !canGoNext}
                    className="home-pagination-btn home-pagination-btn--next"
                    aria-label={t('common.paginationNextLabel')}
                    title={t('common.nextPage')}
                  >
                    {loadingMore ? t('common.loading') : '›'}
                  </button>
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore || !canGoNext}
                    className="home-pagination-btn home-pagination-btn--arrow"
                    aria-label={t('common.lastPage')}
                    title={t('common.lastPage')}
                  >
                    ››
                  </button>
                </div>
              </nav>
            </>
          )}
          {!loading && !error && items.length === 0 && (
            <div
              style={{
                padding: '3rem 2rem',
                textAlign: 'center',
                background: '#fff',
                borderRadius: 12,
                border: '1px solid #e5e7eb',
              }}
            >
              <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                {t('common.noListings')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
